import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Layers,
  GitBranch,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { sectionsApi, optionsApi, Section, Option } from '../../services/schoolApi';
import ConfirmDialog from '../../components/ConfirmDialog';

const SchoolSettings = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);

  // Sections state
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [sectionError, setSectionError] = useState('');
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [sectionForm, setSectionForm] = useState({ name: '', code: '', description: '' });
  const [deleteSectionDialogOpen, setDeleteSectionDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<string | null>(null);

  // Options state
  const [options, setOptions] = useState<Option[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionError, setOptionError] = useState('');
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<Option | null>(null);
  const [optionForm, setOptionForm] = useState({ name: '', code: '', sectionId: '', description: '' });
  const [deleteOptionDialogOpen, setDeleteOptionDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState<string | null>(null);
  const [filterSectionId, setFilterSectionId] = useState('');

  const text = {
    fr: {
      title: 'Paramètres de l\'École',
      description: 'Gérez les sections et les options de votre école',
      tabSections: 'Sections',
      tabOptions: 'Options',
      addSection: 'Ajouter une section',
      editSection: 'Modifier la section',
      addOption: 'Ajouter une option',
      editOption: 'Modifier l\'option',
      name: 'Nom',
      code: 'Code',
      descriptionField: 'Description',
      section: 'Section',
      status: 'Statut',
      actions: 'Actions',
      active: 'Actif',
      inactive: 'Inactif',
      save: 'Enregistrer',
      cancel: 'Annuler',
      loading: 'Chargement...',
      noSections: 'Aucune section trouvée',
      noOptions: 'Aucune option trouvée',
      deleteConfirm: 'Êtes-vous sûr de vouloir supprimer cet élément ?',
      deleteTitle: 'Confirmer la suppression',
      allSections: 'Toutes les sections',
      createdAt: 'Créé le',
    },
    en: {
      title: 'School Settings',
      description: 'Manage your school sections and options',
      tabSections: 'Sections',
      tabOptions: 'Options',
      addSection: 'Add Section',
      editSection: 'Edit Section',
      addOption: 'Add Option',
      editOption: 'Edit Option',
      name: 'Name',
      code: 'Code',
      descriptionField: 'Description',
      section: 'Section',
      status: 'Status',
      actions: 'Actions',
      active: 'Active',
      inactive: 'Inactive',
      save: 'Save',
      cancel: 'Cancel',
      loading: 'Loading...',
      noSections: 'No sections found',
      noOptions: 'No options found',
      deleteConfirm: 'Are you sure you want to delete this item?',
      deleteTitle: 'Confirm Deletion',
      allSections: 'All sections',
      createdAt: 'Created',
    },
  };

  const currentText = text[language as keyof typeof text];

  // ==================== Sections ====================
  const fetchSections = async () => {
    setSectionsLoading(true);
    setSectionError('');
    try {
      const data = await sectionsApi.getAll();
      setSections(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setSectionError(err.message || 'Error');
    } finally {
      setSectionsLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const handleOpenSectionDialog = (section?: Section) => {
    if (section) {
      setEditingSection(section);
      setSectionForm({ name: section.name, code: section.code || '', description: section.description || '' });
    } else {
      setEditingSection(null);
      setSectionForm({ name: '', code: '', description: '' });
    }
    setSectionDialogOpen(true);
  };

  const handleSectionSubmit = async () => {
    try {
      setSectionError('');
      if (editingSection) {
        await sectionsApi.update(editingSection._id, sectionForm);
      } else {
        await sectionsApi.create(sectionForm);
      }
      setSectionDialogOpen(false);
      fetchSections();
    } catch (err: any) {
      setSectionError(err.message || 'Error');
    }
  };

  const handleToggleSection = async (id: string) => {
    try {
      await sectionsApi.toggle(id);
      fetchSections();
    } catch (err: any) {
      setSectionError(err.message || 'Error');
    }
  };

  const handleDeleteSection = async () => {
    if (!sectionToDelete) return;
    try {
      await sectionsApi.delete(sectionToDelete);
      setDeleteSectionDialogOpen(false);
      setSectionToDelete(null);
      fetchSections();
    } catch (err: any) {
      setSectionError(err.message || 'Error');
    }
  };

  // ==================== Options ====================
  const fetchOptions = async () => {
    setOptionsLoading(true);
    setOptionError('');
    try {
      const data = await optionsApi.getAll(filterSectionId || undefined);
      setOptions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setOptionError(err.message || 'Error');
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchOptions();
    }
  }, [activeTab, filterSectionId]);

  const handleOpenOptionDialog = (option?: Option) => {
    if (option) {
      setEditingOption(option);
      setOptionForm({
        name: option.name,
        code: option.code || '',
        sectionId: typeof option.sectionId === 'string' ? option.sectionId : (option.sectionId as any)?._id || '',
        description: option.description || '',
      });
    } else {
      setEditingOption(null);
      setOptionForm({ name: '', code: '', sectionId: '', description: '' });
    }
    setOptionDialogOpen(true);
  };

  const handleOptionSubmit = async () => {
    try {
      setOptionError('');
      if (editingOption) {
        await optionsApi.update(editingOption._id, optionForm);
      } else {
        await optionsApi.create(optionForm);
      }
      setOptionDialogOpen(false);
      fetchOptions();
    } catch (err: any) {
      setOptionError(err.message || 'Error');
    }
  };

  const handleToggleOption = async (id: string) => {
    try {
      await optionsApi.toggle(id);
      fetchOptions();
    } catch (err: any) {
      setOptionError(err.message || 'Error');
    }
  };

  const handleDeleteOption = async () => {
    if (!optionToDelete) return;
    try {
      await optionsApi.delete(optionToDelete);
      setDeleteOptionDialogOpen(false);
      setOptionToDelete(null);
      fetchOptions();
    } catch (err: any) {
      setOptionError(err.message || 'Error');
    }
  };

  const getSectionName = (sectionId: string | any): string => {
    if (typeof sectionId === 'object' && sectionId?.name) return sectionId.name;
    const section = sections.find((s) => s._id === sectionId);
    return section?.name || '-';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 1 }}>
          {currentText.title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {currentText.description}
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': { fontWeight: 600 },
            '& .Mui-selected': { color: '#0D3C4C !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#0D3C4C' },
          }}
        >
          <Tab icon={<Layers size={18} />} iconPosition="start" label={currentText.tabSections} />
          <Tab icon={<GitBranch size={18} />} iconPosition="start" label={currentText.tabOptions} />
        </Tabs>
      </Paper>

      {/* ==================== SECTIONS TAB ==================== */}
      {activeTab === 0 && (
        <Box>
          {sectionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSectionError('')}>
              {sectionError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => handleOpenSectionDialog()}
              sx={{ backgroundColor: '#0D3C4C', '&:hover': { backgroundColor: '#1A5F7A' } }}
            >
              {currentText.addSection}
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.name}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.code}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.descriptionField}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.status}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.createdAt}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sectionsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography sx={{ mt: 2 }}>{currentText.loading}</Typography>
                    </TableCell>
                  </TableRow>
                ) : sections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{currentText.noSections}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sections.map((section) => (
                    <TableRow key={section._id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{section.name}</TableCell>
                      <TableCell>{section.code || '-'}</TableCell>
                      <TableCell>{section.description || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={section.active ? currentText.active : currentText.inactive}
                          color={section.active ? 'success' : 'default'}
                          size="small"
                          onClick={() => handleToggleSection(section._id)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(section.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenSectionDialog(section)}
                          sx={{ color: '#0D3C4C' }}
                        >
                          <Edit size={18} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleSection(section._id)}
                          sx={{ color: section.active ? '#4caf50' : '#9e9e9e' }}
                        >
                          {section.active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSectionToDelete(section._id);
                            setDeleteSectionDialogOpen(true);
                          }}
                          sx={{ color: '#d32f2f' }}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section Dialog */}
          <Dialog open={sectionDialogOpen} onClose={() => setSectionDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editingSection ? currentText.editSection : currentText.addSection}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <TextField
                  label={currentText.name}
                  value={sectionForm.name}
                  onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  label={currentText.code}
                  value={sectionForm.code}
                  onChange={(e) => setSectionForm({ ...sectionForm, code: e.target.value })}
                  fullWidth
                />
                <TextField
                  label={currentText.descriptionField}
                  value={sectionForm.description}
                  onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSectionDialogOpen(false)}>{currentText.cancel}</Button>
              <Button
                onClick={handleSectionSubmit}
                variant="contained"
                disabled={!sectionForm.name}
                sx={{ backgroundColor: '#0D3C4C', '&:hover': { backgroundColor: '#1A5F7A' } }}
              >
                {currentText.save}
              </Button>
            </DialogActions>
          </Dialog>

          <ConfirmDialog
            open={deleteSectionDialogOpen}
            title={currentText.deleteTitle}
            message={currentText.deleteConfirm}
            onConfirm={handleDeleteSection}
            onCancel={() => {
              setDeleteSectionDialogOpen(false);
              setSectionToDelete(null);
            }}
          />
        </Box>
      )}

      {/* ==================== OPTIONS TAB ==================== */}
      {activeTab === 1 && (
        <Box>
          {optionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOptionError('')}>
              {optionError}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>{currentText.section}</InputLabel>
              <Select
                value={filterSectionId}
                onChange={(e) => setFilterSectionId(e.target.value)}
                label={currentText.section}
                size="small"
              >
                <MenuItem value="">{currentText.allSections}</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => handleOpenOptionDialog()}
              sx={{ backgroundColor: '#0D3C4C', '&:hover': { backgroundColor: '#1A5F7A' } }}
            >
              {currentText.addOption}
            </Button>
          </Paper>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.name}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.code}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.section}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.descriptionField}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.status}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.createdAt}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{currentText.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {optionsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography sx={{ mt: 2 }}>{currentText.loading}</Typography>
                    </TableCell>
                  </TableRow>
                ) : options.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{currentText.noOptions}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  options.map((option) => (
                    <TableRow key={option._id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{option.name}</TableCell>
                      <TableCell>{option.code || '-'}</TableCell>
                      <TableCell>{getSectionName(option.sectionId)}</TableCell>
                      <TableCell>{option.description || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={option.active ? currentText.active : currentText.inactive}
                          color={option.active ? 'success' : 'default'}
                          size="small"
                          onClick={() => handleToggleOption(option._id)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(option.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenOptionDialog(option)}
                          sx={{ color: '#0D3C4C' }}
                        >
                          <Edit size={18} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleOption(option._id)}
                          sx={{ color: option.active ? '#4caf50' : '#9e9e9e' }}
                        >
                          {option.active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setOptionToDelete(option._id);
                            setDeleteOptionDialogOpen(true);
                          }}
                          sx={{ color: '#d32f2f' }}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Option Dialog */}
          <Dialog open={optionDialogOpen} onClose={() => setOptionDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editingOption ? currentText.editOption : currentText.addOption}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <TextField
                  label={currentText.name}
                  value={optionForm.name}
                  onChange={(e) => setOptionForm({ ...optionForm, name: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  label={currentText.code}
                  value={optionForm.code}
                  onChange={(e) => setOptionForm({ ...optionForm, code: e.target.value })}
                  required
                  fullWidth
                />
                <FormControl fullWidth required>
                  <InputLabel>{currentText.section}</InputLabel>
                  <Select
                    value={optionForm.sectionId}
                    onChange={(e) => setOptionForm({ ...optionForm, sectionId: e.target.value })}
                    label={currentText.section}
                  >
                    {sections.map((s) => (
                      <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={currentText.descriptionField}
                  value={optionForm.description}
                  onChange={(e) => setOptionForm({ ...optionForm, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOptionDialogOpen(false)}>{currentText.cancel}</Button>
              <Button
                onClick={handleOptionSubmit}
                variant="contained"
                disabled={!optionForm.name || !optionForm.code || !optionForm.sectionId}
                sx={{ backgroundColor: '#0D3C4C', '&:hover': { backgroundColor: '#1A5F7A' } }}
              >
                {currentText.save}
              </Button>
            </DialogActions>
          </Dialog>

          <ConfirmDialog
            open={deleteOptionDialogOpen}
            title={currentText.deleteTitle}
            message={currentText.deleteConfirm}
            onConfirm={handleDeleteOption}
            onCancel={() => {
              setDeleteOptionDialogOpen(false);
              setOptionToDelete(null);
            }}
          />
        </Box>
      )}
    </Container>
  );
};

export default SchoolSettings;
